import { Routes } from '@angular/router';
import {
  redirectToActivePartner,
  requireCapabilities,
  requirePartnerAccess,
  setActivePartnerFromRoute,
} from '@sisoma/auth';

import { Shell } from './core/layout/shell';

/**
 * All feature pages are lazy-loaded children of the shell, so the sidenav and
 * main-area frame persist across navigation. Each route's `title` feeds the
 * document title via `PartnerTitleStrategy`; `pageTitle` data is bound to
 * component inputs by `withComponentInputBinding`.
 *
 * Adding a section = add a child route here + an entry in
 * `core/navigation/nav-items.ts`.
 */
export const routes: Routes = [
  // The 403 page must sit OUTSIDE the partner-access-guarded shell and be
  // matched BEFORE it — otherwise the guard's redirect here would re-trigger
  // the guard (or fall through the shell's `**`) and loop. Rendered standalone,
  // without the sidenav frame.
  {
    path: 'forbidden',
    title: 'Not authorised',
    loadComponent: () =>
      import('./features/not-authorised/not-authorised').then((m) => m.NotAuthorised),
  },
  {
    path: '',
    component: Shell,
    // Coarse gate for the whole surface (fp1-04): logged-out → shared login;
    // logged-in without partner access → `/forbidden`. Per-feature
    // `requireCapabilities(...)` guards attach to individual child routes as
    // real features land (fp1-07+).
    canActivate: [requirePartnerAccess],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'example',
        title: 'Example',
        data: { pageTitle: 'Example' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      {
        path: 'example-group/child',
        title: 'Example Child',
        data: { pageTitle: 'Example Child' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      {
        path: 'example-group/another',
        title: 'Another Child',
        data: { pageTitle: 'Another Child' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      // Settings follows the same dual-path shape as the other partner-scoped
      // features: the bare path canonicalises to the active partner's id URL,
      // which is where the capability gate lives. It is an index of its
      // sub-pages — each of those carries its own capability, so this page
      // shows only what the viewer can actually reach.
      {
        path: 'settings',
        title: 'Settings',
        canActivate: [redirectToActivePartner('settings')],
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
      {
        path: 'partner/:id/settings',
        title: 'Settings',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_settings')],
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
      // Single sign-on is a settings sub-page gated on `partner:manage_sso`
      // rather than the parent's `partner:manage_settings`. That is the whole
      // point of the capability: an Owner can delegate the Entra connection to
      // an IT person without also handing over billing and roles, so someone
      // holding only this one must be able to reach the page.
      {
        path: 'settings/sso',
        title: 'Single sign-on',
        canActivate: [redirectToActivePartner('settings', 'sso')],
        loadComponent: () => import('./features/settings/sso/sso').then((m) => m.SsoSettings),
      },
      {
        path: 'partner/:id/settings/sso',
        title: 'Single sign-on',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_sso')],
        loadComponent: () => import('./features/settings/sso/sso').then((m) => m.SsoSettings),
      },
      // Audit Log is a settings sub-page (PA-AUDIT §6), gated on its own
      // capability rather than the parent's. Same static-alias +
      // `partner/:id/...` pair as the features below.
      {
        path: 'settings/audit',
        title: 'Audit Log',
        canActivate: [redirectToActivePartner('settings', 'audit')],
        loadComponent: () =>
          import('./features/settings/audit-log/audit-log').then((m) => m.AuditLog),
      },
      {
        path: 'partner/:id/settings/audit',
        title: 'Audit Log',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:view_audit')],
        loadComponent: () =>
          import('./features/settings/audit-log/audit-log').then((m) => m.AuditLog),
      },
      // Members, Roles, Invites and Workspaces live at `partner/:id/...`; the
      // sidebar links to the static `members`/`roles`/`invites`/`workspaces`
      // paths, which `redirectToActivePartner(...)` canonicalises to the active
      // partner's id URL (no partner switcher yet).
      {
        path: 'members',
        title: 'Members',
        canActivate: [redirectToActivePartner('members')],
        loadComponent: () => import('./features/members/members').then((m) => m.Members),
      },
      {
        path: 'partner/:id/members',
        title: 'Members',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_members')],
        loadComponent: () => import('./features/members/members').then((m) => m.Members),
      },
      {
        path: 'roles',
        title: 'Roles',
        canActivate: [redirectToActivePartner('roles')],
        loadComponent: () => import('./features/roles/roles').then((m) => m.Roles),
      },
      {
        path: 'partner/:id/roles',
        title: 'Roles',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_roles')],
        loadComponent: () => import('./features/roles/roles').then((m) => m.Roles),
      },
      {
        path: 'invites',
        title: 'Invites',
        canActivate: [redirectToActivePartner('invites')],
        loadComponent: () => import('./features/invites/invites').then((m) => m.Invites),
      },
      {
        path: 'partner/:id/invites',
        title: 'Invites',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:invite')],
        loadComponent: () => import('./features/invites/invites').then((m) => m.Invites),
      },
      {
        path: 'workspaces',
        title: 'Workspaces',
        canActivate: [redirectToActivePartner('workspaces')],
        loadComponent: () => import('./features/workspaces/workspaces').then((m) => m.Workspaces),
      },
      {
        path: 'partner/:id/workspaces',
        title: 'Workspaces',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_workspaces')],
        loadComponent: () => import('./features/workspaces/workspaces').then((m) => m.Workspaces),
      },
      // Content & Categories (PA-CONTENT). Same route-pair convention as the
      // features above; `redirectToActivePartner` takes the sub-path segment by
      // segment, so the alias `/content/categories` canonicalises to
      // `/partner/:id/content/categories`. The list screen sits at
      // `content/library` rather than `content/content` — the group is already
      // called Content, and `content/content` reads as a typo in the URL bar.
      {
        path: 'content/categories',
        title: 'Categories',
        canActivate: [redirectToActivePartner('content', 'categories')],
        loadComponent: () =>
          import('./features/content/categories/categories').then((m) => m.Categories),
      },
      {
        path: 'partner/:id/content/categories',
        title: 'Categories',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('content:manage_categories')],
        loadComponent: () =>
          import('./features/content/categories/categories').then((m) => m.Categories),
      },
      {
        path: 'content/library',
        title: 'Content',
        canActivate: [redirectToActivePartner('content', 'library')],
        loadComponent: () =>
          import('./features/content/library/library').then((m) => m.ContentLibrary),
      },
      {
        path: 'partner/:id/content/library',
        title: 'Content',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('content:view')],
        loadComponent: () =>
          import('./features/content/library/library').then((m) => m.ContentLibrary),
      },
      {
        path: 'billing',
        title: 'Billing',
        canActivate: [redirectToActivePartner('billing')],
        loadComponent: () => import('./features/billing/billing').then((m) => m.Billing),
      },
      {
        path: 'partner/:id/billing',
        title: 'Billing',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_billing')],
        loadComponent: () => import('./features/billing/billing').then((m) => m.Billing),
      },
      // The capability-denial landing (`capabilityDeniedUrl`). A child of the
      // shell on purpose — the opposite call from `/forbidden` above, and safe
      // for the reason that one is not: anyone who reaches a capability guard
      // has already passed `requirePartnerAccess`, so redirecting them inside
      // cannot loop. That placement is the whole point. `MembershipGate` owns
      // this outlet, so for the person whose capabilities are empty *because
      // they have no role* it replaces this page with the No role screen —
      // switcher included, which a route outside the shell cannot offer.
      //
      // It must stay ungated: a `requireCapabilities(...)` here would send its
      // own denials back to itself.
      {
        path: 'no-access',
        title: 'No access',
        loadComponent: () => import('./features/no-access/no-access').then((m) => m.NoAccess),
      },
      {
        path: '**',
        title: 'Page not found',
        loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
